<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useEventListener } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useCarBookingStore } from 'dashboard/stores/carBooking';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TripStep from './steps/TripStep.vue';
import OffersStep from './steps/OffersStep.vue';
import CompareStep from './steps/CompareStep.vue';
import ChoiceStep from './steps/ChoiceStep.vue';
import OfferDetailStep from './steps/OfferDetailStep.vue';
import BookStep from './steps/BookStep.vue';
import BookedStep from './steps/BookedStep.vue';

const { t } = useI18n();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const currentChat = useMapGetter('getSelectedChat');
const allBookingsByContactId = useMapGetter(
  'contactBookings/getAllBookingsByContactId'
);
const carBooking = useCarBookingStore();

const conversationId = computed(() => currentChat.value?.id);

const isOpen = computed(
  () => uiSettings.value.is_car_booking_panel_open && !!conversationId.value
);

const session = computed(() => carBooking.sessionFor(conversationId.value));

const STEP_ORDER = ['trip', 'offers', 'choice', 'book'];

const STEP_COMPONENTS = {
  trip: TripStep,
  offers: OffersStep,
  compare: CompareStep,
  choice: ChoiceStep,
  detail: OfferDetailStep,
  book: BookStep,
  booked: BookedStep,
};

const stepComponent = computed(
  () => STEP_COMPONENTS[session.value.step] || TripStep
);

const activeStepIndex = computed(() => {
  const raw =
    session.value.step === 'detail'
      ? session.value.detailReturn
      : session.value.step;
  // Compare is a side branch off the offers step, not a stage of its own, so it
  // (and a detail view opened from it) keeps the offers crumb lit.
  const step = raw === 'compare' ? 'offers' : raw;
  const index = STEP_ORDER.indexOf(step);
  return index === -1 ? STEP_ORDER.length - 1 : index;
});

const isBusy = computed(() => Object.values(carBooking.uiFlags).some(Boolean));

const canGoToStep = index =>
  index < activeStepIndex.value &&
  session.value.step !== 'booked' &&
  !isBusy.value;

const goToStep = step => carBooking.setStep(conversationId.value, step);

const steps = computed(() => [
  { key: 'trip', label: t('CONVERSATION.CAR_BOOKING.STEPS.TRIP') },
  { key: 'offers', label: t('CONVERSATION.CAR_BOOKING.STEPS.OFFERS') },
  { key: 'choice', label: t('CONVERSATION.CAR_BOOKING.STEPS.CHOICE') },
  { key: 'book', label: t('CONVERSATION.CAR_BOOKING.STEPS.BOOK') },
]);

const contactId = computed(() => currentChat.value?.meta?.sender?.id);

const previousRentalsCount = computed(() => {
  if (!contactId.value) return 0;
  return (allBookingsByContactId.value(contactId.value) || []).filter(
    booking => booking.booking_type === 'car_rental'
  ).length;
});

const close = () => updateUISettings({ is_car_booking_panel_open: false });

useEventListener(window, 'keydown', e => {
  if (e.key === 'Escape' && isOpen.value) close();
});

watch(
  () => [isOpen.value, contactId.value],
  ([open, id]) => {
    if (open && id) {
      store.dispatch('contactBookings/get', { contactId: id });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-0 md:p-6"
  >
    <div
      v-on-click-outside="() => close()"
      class="bg-n-surface-2 flex flex-col overflow-hidden w-full h-full md:h-[85vh] md:w-[75vw] md:max-w-6xl md:rounded-xl shadow-xl border border-n-weak"
    >
      <header
        class="flex items-center justify-between gap-2 border-b border-n-weak px-4 py-3"
      >
        <div class="flex min-w-0 flex-col">
          <h2 class="m-0 text-base font-medium text-n-slate-12">
            {{ t('CONVERSATION.CAR_BOOKING.TITLE') }}
          </h2>
          <span v-if="previousRentalsCount > 0" class="text-xs text-n-slate-11">
            {{
              t('CONVERSATION.CAR_BOOKING.PREVIOUS_RENTALS', {
                count: previousRentalsCount,
              })
            }}
          </span>
        </div>
        <NextButton
          ghost
          slate
          sm
          icon="i-lucide-x"
          :aria-label="t('CONVERSATION.CAR_BOOKING.CLOSE')"
          @click="close"
        />
      </header>

      <nav class="flex items-center gap-1 border-b border-n-weak px-4 py-2.5">
        <template v-for="(step, index) in steps" :key="step.key">
          <button
            type="button"
            class="text-xs font-medium disabled:cursor-default"
            :class="[
              index <= activeStepIndex ? 'text-n-slate-12' : 'text-n-slate-10',
              canGoToStep(index) && 'cursor-pointer hover:underline',
            ]"
            :disabled="!canGoToStep(index)"
            :aria-current="index === activeStepIndex ? 'step' : undefined"
            @click="goToStep(step.key)"
          >
            {{ step.label }}
          </button>
          <span
            v-if="index < steps.length - 1"
            class="i-lucide-chevron-right size-3 text-n-slate-10"
          />
        </template>
      </nav>

      <div
        :class="
          session.step === 'offers'
            ? 'flex-1 min-h-0 overflow-hidden'
            : 'flex-1 overflow-y-auto p-4'
        "
      >
        <component :is="stepComponent" :conversation-id="conversationId" />
      </div>
    </div>
  </div>
  <template v-else />
</template>
